import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Avatar,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Edit as EditIcon,
  Download as DownloadIcon,
  Lock as LockIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { authService } from "../../services/authService";
import api from "../../services/api";
import SessionSecurityPanel from "../../components/admin/SessionSecurityPanel";
import AdminSeguridadPanel from "../../components/admin/AdminSeguridadPanel";
import AdminRHPanel from "../../components/admin/AdminRHPanel";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatRelativeTime } from "../../utils/dateUtils";

const UserProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Tab activa (0 = Mi Perfil, 1 = Seguridad, 2 = RH Overview, 3 = Candidatos, 4 = Puestos)
  const getTabFromParams = (params) => {
    const tab = params.get("tab");
    if (tab === "rh") return 2;
    if (tab === "seguridad") return 1;
    return 0;
  };

  const [tabValue, setTabValue] = useState(() => getTabFromParams(searchParams));

  // Sincronizar cuando el usuario navega entre rutas del mismo componente
  useEffect(() => {
    setTabValue(getTabFromParams(searchParams));
  }, [searchParams]);

  // true cuando estamos en cualquier sub-sección de RH
  const isRHMode = tabValue >= 2;

  // Estados de edición
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({
    nombre_completo: user?.nombre_completo || "",
    email: user?.email || "",
    telefono: user?.telefono || "",
    ubicacion: user?.ubicacion || "",
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Estados de cambio de contraseña
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  // Estados de estadísticas
  const [stats, setStats] = useState([
    { label: "Eventos Totales", value: "0", color: "#1a237e" },
    { label: "Colaboraciones", value: "0", color: "#2e7d32" },
    { label: "Horas Trabajadas", value: "0", color: "#c62828" },
    { label: "Calificación", value: "N/A", color: "#f57c00" },
  ]);
  // eslint-disable-next-line no-unused-vars
  const [loadingStats, setLoadingStats] = useState(true);

  // Estados de actividad
  const [activityLog, setActivityLog] = useState([]);

  // Estados de mensajes
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Cargar estadísticas al montar
  useEffect(() => {
    loadUserStats();
  }, []);

  const loadUserStats = async () => {
    try {
      setLoadingStats(true);
      const response = await api.get("/usuarios/stats");

      if (response.data?.success) {
        const statsData = response.data.stats;
        setStats([
          {
            label: "Eventos Totales",
            value: String(statsData.eventos_totales || 0),
            color: "#1a237e",
          },
          {
            label: "Colaboraciones",
            value: String(statsData.colaboraciones || 0),
            color: "#2e7d32",
          },
          {
            label: "Horas Trabajadas",
            value: String(statsData.horas_trabajadas || 0),
            color: "#c62828",
          },
          {
            label: "Calificación",
            value: statsData.calificacion || "N/A",
            color: "#f57c00",
          },
        ]);

        // Cargar actividad reciente
        if (response.data.actividad_reciente) {
          setActivityLog(
            response.data.actividad_reciente.map((act) => ({
              action: act.action,
              timestamp: formatRelativeTime(act.timestamp),
              icon: act.icon || "✓",
            })),
          );
        }
      }
    } catch (err) {
      console.error("Error cargando estadísticas:", err);
    } finally {
      setLoadingStats(false);
    }
  };

  // Usar utilidad centralizada de fechas con zona horaria CDMX

  // Guardar perfil
  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      // TODO: Implementar endpoint de actualización de perfil
      setSnackbar({
        open: true,
        message: "Perfil actualizado correctamente",
        severity: "success",
      });
      setEditOpen(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error al actualizar perfil",
        severity: "error",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  // Cambiar contraseña
  const handleChangePassword = async () => {
    setPasswordError("");

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("Las contraseñas no coinciden");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }

    try {
      setSavingPassword(true);
      const response = await api.post("/auth/change-password", {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      });

      if (response.data?.success) {
        setSnackbar({
          open: true,
          message: "Contraseña actualizada correctamente",
          severity: "success",
        });
        setPasswordOpen(false);
        setPasswordData({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        setPasswordError(
          response.data?.message || "Error al cambiar contraseña",
        );
      }
    } catch (err) {
      setPasswordError(
        err.response?.data?.message || "Error al cambiar contraseña",
      );
    } finally {
      setSavingPassword(false);
    }
  };

  // Cerrar sesiones en otros dispositivos
  const handleCloseOtherSessions = async () => {
    try {
      await authService.closeAllOtherSessions();
      setSnackbar({
        open: true,
        message: "Sesiones en otros dispositivos cerradas",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error al cerrar otras sesiones",
        severity: "error",
      });
    }
  };

  // Descargar datos del usuario
  const handleDownloadData = () => {
    const userData = {
      perfil: {
        nombre: user?.nombre_completo,
        email: user?.email,
        username: user?.username,
        rol: user?.rol,
        telefono: user?.telefono || "No proporcionado",
        ubicacion: user?.ubicacion || "No especificada",
      },
      estadisticas: stats.reduce((acc, stat) => {
        acc[stat.label] = stat.value;
        return acc;
      }, {}),
      fecha_descarga: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(userData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mis_datos_${user?.username}_${new Date().toISOString().split("T")[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);

    setSnackbar({
      open: true,
      message: "Datos descargados correctamente",
      severity: "success",
    });
  };

  // Ir a configuración
  const handleGoToSettings = () => {
    navigate("/admin/settings");
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header con Avatar y Info Básica */}
      <Paper
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mb: 4,
          background: "linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)",
        }}
      >
        <Box sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "center", sm: "flex-start" },
          gap: { xs: 2, sm: 3 },
        }}>
          {/* Avatar */}
          <Avatar
            src={user?.colaborador?.foto_url || undefined}
            sx={{
              width: { xs: 110, sm: 120 },
              height: { xs: 110, sm: 120 },
              fontSize: { xs: 44, sm: 48 },
              background: "linear-gradient(135deg, #00C9FF 0%, #92FE9D 100%)",
              flexShrink: 0,
            }}
          >
            {user?.colaborador?.nombre_completo?.charAt(0) || user?.username?.charAt(0)}
          </Avatar>

          {/* Nombre + usuario + chips */}
          <Box sx={{ flex: 1, textAlign: { xs: "center", sm: "left" }, width: { xs: "100%", sm: "auto" } }}>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: "#fff", mb: 0.5, fontSize: { xs: "1.4rem", sm: "2rem", md: "2.5rem" } }}
            >
              {user?.colaborador?.nombre_completo || user?.username}
            </Typography>
            <Typography variant="body1" sx={{ color: "#90caf9", mb: 1.5 }}>
              @{user?.username}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: { xs: "center", sm: "flex-start" } }}>
              <Chip
                label={`Rol: ${user?.rol}`}
                color="primary"
                variant="outlined"
                sx={{ color: "#90caf9", borderColor: "#90caf9" }}
              />
              <Chip
                label="Activo"
                icon={<CheckCircleIcon />}
                sx={{ backgroundColor: "#2e7d32", color: "#fff" }}
              />
            </Box>
          </Box>

          {/* Botones */}
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: { xs: "100%", sm: "auto" },
          }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditOpen(true)}
              sx={{
                background: "linear-gradient(45deg, #00C9FF 0%, #92FE9D 100%)",
                color: "#000",
                fontWeight: "bold",
                borderRadius: "50px",
              }}
            >
              Editar Perfil
            </Button>
            <Button
              variant="outlined"
              startIcon={<LockIcon />}
              onClick={() => setPasswordOpen(true)}
              sx={{
                borderColor: "#90caf9",
                color: "#90caf9",
                borderRadius: "50px",
                "&:hover": { backgroundColor: "rgba(144, 202, 249, 0.04)" },
              }}
            >
              Cambiar Contraseña
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabs de navegación */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        {!isRHMode ? (
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            sx={{ "& .MuiTab-root": { fontWeight: 600, textTransform: "none", minWidth: 120 } }}
          >
            <Tab label="👤 Mi Perfil" />
            <Tab label="🔒 Seguridad" />
          </Tabs>
        ) : (
          <Tabs
            value={tabValue - 2}
            onChange={(_, v) => setTabValue(v + 2)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                fontWeight: 600,
                textTransform: "none",
                minWidth: { xs: 90, sm: 140 },
                fontSize: { xs: "0.78rem", sm: "0.875rem" },
                px: { xs: 1.5, sm: 2 },
              },
              "& .MuiTabs-indicator": { bgcolor: "#6366f1" },
              "& .Mui-selected": { color: "#a5b4fc !important" },
            }}
          >
            <Tab label="👥 Recursos Humanos" />
            <Tab label="📋 Candidatos" />
            <Tab label="💼 Puestos disponibles" />
            <Tab label="📅 Entrevistas" />
          </Tabs>
        )}
      </Box>

      {/* ── Tab 0: Mi Perfil ── */}
      {tabValue === 0 && (
        <>
          {/* Panel de Seguridad de Sesiones */}
          <Box sx={{ mb: 4 }}>
            <SessionSecurityPanel />
          </Box>

      {/* Información Detallada */}
      <Grid container spacing={3}>
        {/* Datos Personales */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              📋 Información Personal
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: "grid", gap: 2 }}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body2">{user?.email}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Teléfono
                </Typography>
                <Typography variant="body2">
                  {user?.telefono || "No proporcionado"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Ubicación
                </Typography>
                <Typography variant="body2">
                  {user?.ubicacion || "No especificada"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Estado
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#2e7d32", fontWeight: "bold" }}
                >
                  ✓ Activo
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Acciones Rápidas */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              ⚡ Acciones Rápidas
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: "grid", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                fullWidth
                sx={{ justifyContent: "flex-start" }}
                onClick={handleDownloadData}
              >
                Descargar Mis Datos
              </Button>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                fullWidth
                sx={{ justifyContent: "flex-start" }}
                onClick={handleGoToSettings}
              >
                Preferencias de Notificación
              </Button>
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                fullWidth
                sx={{ justifyContent: "flex-start" }}
                onClick={() => setPasswordOpen(true)}
              >
                Seguridad
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                fullWidth
                sx={{ justifyContent: "flex-start" }}
                onClick={handleCloseOtherSessions}
              >
                Cerrar Sesión en Otros Dispositivos
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Historial de Actividad */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              📊 Actividad Reciente
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "rgba(0, 0, 0, 0.05)" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>Acción</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Fecha y Hora
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", textAlign: "right" }}>
                      Estado
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activityLog.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                        <Typography color="textSecondary">
                          No hay actividad reciente
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    activityLog.map((log, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{log.action}</TableCell>
                        <TableCell sx={{ color: "#666" }}>
                          <AccessTimeIcon sx={{ fontSize: 14, mr: 1 }} />
                          {log.timestamp}
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "right", color: "#2e7d32" }}
                        >
                          <CheckCircleIcon sx={{ fontSize: 18 }} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
        </>
      )}

      {/* ── Tab 1: Seguridad ── */}
      {tabValue === 1 && <AdminSeguridadPanel />}

      {/* ── Tab 2: RH Overview ── */}
      {tabValue === 2 && <AdminRHPanel activeSection="overview" />}

      {/* ── Tab 3: Candidatos ── */}
      {tabValue === 3 && <AdminRHPanel activeSection="candidatos" />}

      {/* ── Tab 4: Puestos disponibles ── */}
      {tabValue === 4 && <AdminRHPanel activeSection="puestos" />}

      {/* ── Tab 5: Entrevistas ── */}
      {tabValue === 5 && <AdminRHPanel activeSection="entrevistas" />}

      {/* Dialog de Edición */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar Perfil</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="Nombre Completo"
            value={editData.nombre_completo}
            onChange={(e) =>
              setEditData({ ...editData, nombre_completo: e.target.value })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={editData.email}
            onChange={(e) =>
              setEditData({ ...editData, email: e.target.value })
            }
            margin="normal"
          />
          <TextField
            fullWidth
            label="Teléfono"
            value={editData.telefono}
            onChange={(e) =>
              setEditData({
                ...editData,
                telefono: e.target.value.replace(/\D/g, "").slice(0, 10),
              })
            }
            margin="normal"
            inputProps={{ maxLength: 10, inputMode: "numeric" }}
            helperText={
              editData.telefono && editData.telefono.length < 10
                ? `${editData.telefono.length}/10 dígitos`
                : ""
            }
          />
          <TextField
            fullWidth
            label="Ubicación"
            value={editData.ubicacion}
            onChange={(e) =>
              setEditData({ ...editData, ubicacion: e.target.value })
            }
            margin="normal"
          />
          <Alert severity="info" sx={{ mt: 3 }}>
            Los cambios se guardarán inmediatamente. Tu perfil estará
            actualizado en toda la plataforma.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={savingProfile}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={savingProfile}
          >
            {savingProfile ? <CircularProgress size={20} /> : "Guardar Cambios"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Cambio de Contraseña */}
      <Dialog
        open={passwordOpen}
        onClose={() => {
          setPasswordOpen(false);
          setPasswordError("");
          setPasswordData({
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LockIcon color="primary" />
            Cambiar Contraseña
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Contraseña Actual"
            type={showOldPassword ? "text" : "password"}
            value={passwordData.oldPassword}
            onChange={(e) =>
              setPasswordData({ ...passwordData, oldPassword: e.target.value })
            }
            margin="normal"
            InputProps={{
              endAdornment: (
                <Button
                  size="small"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                >
                  {showOldPassword ? <VisibilityOff /> : <Visibility />}
                </Button>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Nueva Contraseña"
            type={showNewPassword ? "text" : "password"}
            value={passwordData.newPassword}
            onChange={(e) =>
              setPasswordData({ ...passwordData, newPassword: e.target.value })
            }
            margin="normal"
            helperText="Mínimo 6 caracteres"
            InputProps={{
              endAdornment: (
                <Button
                  size="small"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </Button>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Confirmar Nueva Contraseña"
            type={showNewPassword ? "text" : "password"}
            value={passwordData.confirmPassword}
            onChange={(e) =>
              setPasswordData({
                ...passwordData,
                confirmPassword: e.target.value,
              })
            }
            margin="normal"
            error={
              passwordData.confirmPassword &&
              passwordData.newPassword !== passwordData.confirmPassword
            }
            helperText={
              passwordData.confirmPassword &&
              passwordData.newPassword !== passwordData.confirmPassword
                ? "Las contraseñas no coinciden"
                : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPasswordOpen(false);
              setPasswordError("");
              setPasswordData({
                oldPassword: "",
                newPassword: "",
                confirmPassword: "",
              });
            }}
            disabled={savingPassword}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={
              savingPassword ||
              !passwordData.oldPassword ||
              !passwordData.newPassword ||
              !passwordData.confirmPassword
            }
          >
            {savingPassword ? (
              <CircularProgress size={20} />
            ) : (
              "Cambiar Contraseña"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensajes */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserProfile;
